import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {Observable, Subscription} from 'rxjs';
import { ADMINISTRATOR, CUSTOMER_SUCCESS, UserService } from '../../shared/services/user.service';
import { Recharge, INVOICE_FAILED, RECHARGE_TYPE_DOMAIN_ADDON } from '../../models/Recharge';
import { User } from '../../models/User';
import { ConfirmationService } from '../../shared/components/confirmation-modal/confirmation.service';
import { ToastService } from '../../shared/components/toast/toast.service';
import { PaginatedResult } from '../../shared/model/paginated-result';
import { AuthService } from '../../shared/services/auth.service';
import { RechargesService } from '../../shared/services/recharges.service';
import { UserCreditInfo } from 'src/app/models/UserCreditInfo';
import {FormControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {RechargeFilter} from '../../models/RechargeFilter';

/** Recarga cancelada no backend (CU-86akm2u6q). */
export const PAYMENT_CANCELLED = 'cancelled';

export const PAYMENT_STATUS_LABELS: { [status: string]: string } = {
  rejected: 'Pagamento rejeitado',
  [PAYMENT_CANCELLED]: 'Pagamento cancelado',
};
import { escapeHtml } from 'src/app/shared/utils/escape-html';

@Component({
  selector: 'app-recharges',
  templateUrl: './recharges.component.html',
  styleUrls: ['./recharges.component.scss'],
})
export class RechargesComponent implements OnInit, OnDestroy {
  @ViewChild('modalDelete', {static: true}) modalDelete: ElementRef<any>;
  @ViewChild('modalTransfer', {static: true}) modalTransfer: ElementRef<any>;
  @ViewChild('userInput') userInput: ElementRef<HTMLInputElement>;

  timer: any = null;
  userControl = new FormControl<User | null>(null);
  filteredUser$!: Observable<User[]>;

  deleteForm: UntypedFormGroup;
  transferForm: UntypedFormGroup;

  addPage = 'new';
  page = 1;
  perPage = 10;
  user: User;
  users: User[];

  pixImage: string;
  pixCode: string;

  invalidTime: boolean;

  startDate: string;
  endDate: string;

  startedMonth: Date;
  endedMonth: Date;

  text: string;
  creditInfo: UserCreditInfo;

  invertedOrder = true;
  orderBy = 'created_at';
  author = '';

  recharge: Recharge;

  result: PaginatedResult<Recharge[]>;
  loading = false;
  subscriptions: Subscription[] = [];

  get administrator(): boolean {
    return this.user.profile_id === ADMINISTRATOR;
  }

  get customer_success(): boolean {
    return this.user.profile_id === CUSTOMER_SUCCESS;
  }

  constructor(
    private router: Router,
    private modalService: NgbModal,
    private authService: AuthService,
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private toastService: ToastService,
    private activatedRoute: ActivatedRoute,
    private rechargesService: RechargesService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.user = this.authService.getUser();
    this.activatedRoute.queryParams.subscribe(params => {
      const { start_date, end_date, author } = params;
      this.startedMonth = start_date ? this.parseDate(start_date) : new Date();
      this.endedMonth = end_date ? this.parseDate(end_date) : new Date();
      this.author = author || this.author;
    });

    this.subscriptions.push(
      this.activatedRoute.queryParams.subscribe((params) => {
        const { page, per_page, start_date, end_date, orderBy, invertedOrder } = params;

        this.page = page || this.page;
        this.perPage = per_page || this.perPage;
        this.startDate = start_date ?? new Date();
        this.endDate = end_date ?? new Date();
        this.orderBy = orderBy || this.orderBy;
        this.invertedOrder = invertedOrder !== undefined ? JSON.parse(invertedOrder) : this.invertedOrder;
        this.listPaginated();
      }),

      this.authService.userChanged.subscribe(() => this.listPaginated())
    );

    this.deleteForm = this.formBuilder.group({
      reason: [Validators.required]
    });

    this.transferForm = this.formBuilder.group({
      reason: [Validators.required],
      to_user: [Validators.required, Validators.minLength(36)]
    });

    this.userControl.valueChanges.subscribe(user => {
      this.author = null;
      if (user) {
        this.author = user.email;
      }
    });
  }

  filterUser(): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }

    const filterValue = this.userInput.nativeElement.value?.trim().toLowerCase();

    const user = this.userControl.value;
    if (user && filterValue.length < this.displayUser(user).length) {
      this.userControl.patchValue(null);
      this.filteredUser$ = null;
      return;
    }

    if (filterValue.length >= 3) {
      this.timer = setTimeout(() => {
        this.filteredUser$ = this.userService.filter(filterValue);
      }, 200);
    }
  }

  displayUser(user?: User): string {
    if (user) {
      return `${user.name} (${user.email})`;
    }

    return '';
  }


  parseDate(dateStr: string): Date {
    if (!dateStr) {
      return new Date();
    }
    const dateParts = dateStr.split('-').map(part => parseInt(part, 10));
    return new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
  }

  // Serializa a data pelo calendário LOCAL (Brasília), não em UTC. Usar
  // toISOString() aqui causa off-by-one após ~21h BRT: a data em UTC já virou o
  // dia seguinte, então o filtro esconde as recargas criadas no fim do dia.
  private toLocalYmd(d: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  isFailed(status: number): boolean {
    return status === INVOICE_FAILED;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  listPaginated(): void {
    const filter = new RechargeFilter();
    filter.orderDirection = this.invertedOrder;
    filter.order = this.orderBy;
    filter.author = this.author;
    filter.text = this.text;

    if (this.startedMonth) {
      filter.startDate = this.toLocalYmd(this.startedMonth);
    }

    if (this.endedMonth) {
      filter.endDate = this.toLocalYmd(this.endedMonth);
    }

    if (this.administrator || this.customer_success) {
      this.addPage = 'add';
    }

    this.loading = true;
    this.subscriptions.push(
      this.rechargesService
        .listPaginated(this.page, this.perPage, filter)
        .subscribe({
          next: (result) => {
            this.loading = false;
            this.result = result;
          },
          error: () => { this.loading = false; },
        })
    );
  }

  pageChanged(page: number): void {
    const queryParams: any = { page, per_page: this.perPage };
    if (this.text) {
      queryParams.text = this.text;
    }

    if (this.startedMonth) {
      queryParams.start_date = this.toLocalYmd(this.startedMonth);
    }

    if (this.endedMonth) {
      queryParams.end_date = this.toLocalYmd(this.endedMonth);
    }

    if (this.author) {
      queryParams.author = this.author;
    }

    if (this.endDate >= this.startDate || !this.startDate && this.endDate || !this.endDate && this.startDate) {
      this.invalidTime = false;
      this.router.navigate(['/recharges'], { queryParams });
    } else {
      this.invalidTime = true;
    }
  }

  invertOrder(): void {
    this.router.navigate(['.'], {
      relativeTo: this.activatedRoute,
      queryParams: {
        invertedOrder: !this.invertedOrder
      },
      queryParamsHandling: 'merge'
    });
  }

  filterChanged() {
    this.router.navigate(['.'], {
      relativeTo: this.activatedRoute,
      queryParams: {
        orderBy: this.orderBy,
        author: this.author
      },
      queryParamsHandling: 'merge'
    })
  }

  showQRCode(modalQRCode: any, recharge: Recharge): any {
    this.subscriptions.push(
      this.rechargesService.getPixImage(recharge.id).subscribe((pixImage) => {
        this.pixImage = pixImage.url_qrcode_pix;
        this.pixCode = pixImage.pix_code;
        this.modalService.open(modalQRCode);
      })
    );
  }

  pixCopy(pixCode: string): void {
    navigator.clipboard.writeText(pixCode);
    this.toastService.success('Código PIX copiado para a área de transferência!');
  }

  /**
   * Recarga cancelada (CU-86akm2u6q): o backend grava `payment_status =
   * 'cancelled'` com `confirmated_at` nulo, e confirmar créditos nela responde
   * 409. Sem este caso a linha caía em "Créditos Pendentes" com o botão de
   * confirmar à vista.
   */
  isCancelled(recharge: Recharge): boolean {
    return recharge.payment_status === PAYMENT_CANCELLED;
  }

  /** Só recarga pendente de verdade pode ter os créditos confirmados. */
  canConfirm(recharge: Recharge): boolean {
    return !recharge.confirmated_at && !this.isCancelled(recharge);
  }

  /** Situação do pagamento, quando ela é o motivo de não haver crédito. */
  paymentStatusLabel(recharge: Recharge): string | null {
    if (recharge.confirmated_at) {
      return null;
    }

    return PAYMENT_STATUS_LABELS[recharge.payment_status] || null;
  }

  /** A coluna "Situação" de quem não é admin. */
  clientStatusLabel(recharge: Recharge): string {
    if (recharge.confirmated_at) {
      return 'Créditos confirmados';
    }

    return this.paymentStatusLabel(recharge) || 'Créditos Pendentes';
  }

  confirmCredits(recharge: Recharge): void {
    const message = recharge.type === RECHARGE_TYPE_DOMAIN_ADDON
      ? `Deseja confirmar o pagamento do add-on <b>Domínio Próprio</b> de <b>${escapeHtml(recharge.user.name)}</b>, valor <b>R$ ${recharge.total}</b>? O provisionamento do domínio inicia automaticamente.`
      : `Deseja realmente confirmar os créditos do usuário <b>${escapeHtml(recharge.user.name)}</b>, valor <b>R$ ${recharge.total}</b>?`;

    this.confirmationService.show(message, () => this.confirm(recharge.id));
  }

  confirm(id: string): void {
    this.subscriptions.push(
      this.rechargesService.confirm(id).subscribe({
        next: (recharge) => {
          this.toastService.success('Créditos confirmados com sucesso!');
          const index = this.result.data.findIndex((r) => r.id === recharge.id);
          this.result.data[index].confirmated_at = recharge.confirmated_at;
        },
        error: (error: HttpErrorResponse) => {
          // 409: a recarga foi cancelada enquanto a lista estava aberta. A
          // mensagem do backend diz isso melhor do que um erro genérico, e a
          // linha passa a mostrar o cancelamento.
          if (error?.status === 409) {
            this.toastService.error(error.error?.message || 'Esta recarga foi cancelada e não pode ser confirmada.');
            const row = this.result?.data.find((r) => r.id === id);
            if (row) {
              row.payment_status = PAYMENT_CANCELLED;
            }
            return;
          }

          if (error?.status !== 422 && error?.status !== 0) {
            this.toastService.error(error?.error?.message || 'Não foi possível confirmar os créditos.');
          }
        },
      })
    );
  }

  confirmDelete(recharge: Recharge): void {
    this.confirmationService.show(
      `Deseja realmente excluir a recarga de créditos do usuário <b>${escapeHtml(recharge.user.name)}</b>, valor <b>R$ ${recharge.total}</b>?`,
      () => this.delete(recharge.id)
    );
  }

  delete(id: string, reason?: string): void {
    this.modalService.dismissAll();
    this.subscriptions.push(
      this.rechargesService.delete(id, reason).subscribe(() => {
        this.toastService.success('Recarga de créditos excluída com sucesso!');
        this.listPaginated();
      })
    )
  }

  cancel() {
    const reason = this.deleteForm.get('reason').value;

    if (!this.deleteForm.valid) {
      return this.toastService.error('Por favor, preencha os campos.');
    }

    this.delete(this.recharge.id, reason);
  }

  transfer (): void {
    const reason = this.transferForm.controls.reason.getRawValue();
    const to_user = this.transferForm.controls.to_user.getRawValue();

    if (!reason || !to_user) {
      return this.toastService.error('Por favor, preencha os campos.');
    }

    if (to_user == this.recharge.user_id) {
      return this.toastService.error('O usuário selecionado é inválido.')
    }

    if (this.transferForm && this.transferForm.valid) {
      this.modalService.dismissAll();
      this.rechargesService.transfer(this.recharge.id, reason, to_user).subscribe((response) => {
        if (response.success) {
          this.toastService.success('Recarga de créditos transferida com sucesso!');
          this.listPaginated();
        }else{
          this.toastService.error('Falha ao transferir recarga de créditos!')
        }
      })
    }
  }

  getTotalCredits(userId: string, recharge: Recharge) {
    this.rechargesService.getUserCreditInfo(userId).subscribe((creditInfo) => {
      this.creditInfo = creditInfo;
      let realValue = 0;
      if (creditInfo.credits >= recharge.credits) {
        realValue = recharge.credits;
      } else {
        realValue = creditInfo.credits;
      }
    });
  }

  newActionModal(action: string, modal: any, recharge: Recharge): void {
    if (action === 'transfer') {
      this.getTotalCredits(recharge.user_id, recharge);
    }
    this.deleteForm.reset();
    this.transferForm.reset();
    this.recharge = recharge;
    this.modalService.open(modal);
  }
}
